"use client";

import React, { useState } from "react";
import { TagItem } from "@/types";
import { formatGBP, cn } from "@/lib/utils";
import { Tag, Lock, ExternalLink } from "lucide-react";

interface TagOverlayProps {
  tags: TagItem[];
  className?: string;
  defaultAllOpen?: boolean;
}

export function TagOverlay({
  tags,
  className,
  defaultAllOpen = false,
}: TagOverlayProps) {
  const [allOpen, setAllOpen] = useState(defaultAllOpen);
  const [activeTagId, setActiveTagId] = useState<string | null>(null);

  const toggleAll = () => {
    setAllOpen((prev) => !prev);
    setActiveTagId(null);
  };

  // Pre-calculate positions with basic collision separation
  const positions = React.useMemo(() => {
    const pos = tags.map(t => ({
      x: (t.bbox.x + t.bbox.w / 2) * 100,
      y: (t.bbox.y + t.bbox.h / 2) * 100
    }));
    
    // Simple iterative relaxation to push apart overlapping tags
    for (let iter = 0; iter < 10; iter++) {
      for (let i = 0; i < pos.length; i++) {
        for (let j = i + 1; j < pos.length; j++) {
          const dx = pos[i].x - pos[j].x;
          const dy = pos[i].y - pos[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const minDist = 8; // min percentage distance (roughly 40px on mobile)
          
          if (dist < minDist && dist > 0.01) {
            const pushX = (dx / dist) * (minDist - dist) * 0.5;
            const pushY = (dy / dist) * (minDist - dist) * 0.5;
            pos[i].x += pushX;
            pos[i].y += pushY;
            pos[j].x -= pushX;
            pos[j].y -= pushY;
          }
        }
      }
    }
    return pos;
  }, [tags]);

  return (
    <div
      className={cn(
        "absolute inset-0 pointer-events-none select-none z-10 overflow-hidden",
        className
      )}
      aria-label="Interactive Product Tags Overlay"
    >
      {/* Visually hidden list for accessibility */}
      <ul className="sr-only">
        {tags.map((tag) => (
          <li key={`sr-${tag.id}`}>
            {tag.label}: {formatGBP(tag.price_gbp)} from {tag.retailer}
          </li>
        ))}
      </ul>

      {/* Render interactive dots & cards */}
      {tags.map((tag, index) => {
        const posX = Math.max(2, Math.min(98, positions[index].x));
        const posY = Math.max(2, Math.min(98, positions[index].y));

        const isOpen = allOpen || activeTagId === tag.id;

        return (
          <div
            key={tag.id}
            className="absolute transition-all duration-300 pointer-events-auto"
            style={{
              left: `${posX}%`,
              top: `${posY}%`,
              transform: "translate(-50%, -50%)",
              zIndex: isOpen ? 30 : 20,
            }}
          >
            {/* Tag Dot */}
            <button
              onClick={() => setActiveTagId((curr) => (curr === tag.id ? null : tag.id))}
              onMouseEnter={() => !allOpen && setActiveTagId(tag.id)}
              onMouseLeave={() => !allOpen && setActiveTagId(null)}
              aria-label={`${tag.label}, ${formatGBP(tag.price_gbp)} from ${tag.retailer}`}
              aria-expanded={isOpen}
              className={cn(
                "relative group flex items-center justify-center w-6 h-6 rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-clay focus:ring-offset-2",
                tag.locked ? "text-ink-soft" : "text-clay"
              )}
            >
              {/* Outer pulsing ring */}
              <span
                className={cn(
                  "absolute w-3.5 h-3.5 rounded-full",
                  tag.locked ? "bg-ink-soft/40" : "bg-clay animate-tag-pulse"
                )}
              />
              {/* Core inner dot */}
              <span
                className={cn(
                  "relative w-2.5 h-2.5 rounded-full transition-transform group-hover:scale-125",
                  tag.locked ? "bg-ink-soft" : "bg-clay"
                )}
              />
            </button>

            {/* Tag Card Popup */}
            {isOpen && (
              <div
                style={{
                  animationDelay: allOpen ? `${index * 40}ms` : "0ms",
                }}
                className={cn(
                  "absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-48 sm:w-56 p-2.5 rounded-[10px] bg-paper/95 backdrop-blur-md shadow-card border border-line text-left transition-all duration-200 animate-in fade-in zoom-in-95",
                  "after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-4 after:border-transparent after:border-t-paper/95"
                )}
              >
                <div className="flex items-start justify-between gap-1 mb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-ink line-clamp-1">
                    {tag.label}
                  </span>
                  {tag.locked && <Lock className="w-3 h-3 text-ink-soft flex-shrink-0" />}
                </div>

                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="font-mono text-xs font-bold text-brass-deep tabular-nums">
                    {formatGBP(tag.price_gbp)}
                  </span>
                  <span className="text-[11px] text-ink-soft truncate">
                    {tag.retailer}
                  </span>
                </div>

                {tag.product_title && (
                  <p className="text-[11px] text-ink-soft mt-1 line-clamp-1">
                    {tag.product_title}
                  </p>
                )}

                {tag.in_stock && (
                  <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-line/60">
                    <span className="w-1.5 h-1.5 rounded-full bg-sage" />
                    <span className="text-[10px] font-medium text-sage">In Stock (UK)</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Signature "Show Price Tags" Toggle */}
      <div className="absolute bottom-4 right-4 pointer-events-auto z-40">
        <button
          onClick={toggleAll}
          className={cn(
            "flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium backdrop-blur-md transition-all shadow-sm border cursor-pointer",
            allOpen
              ? "bg-clay text-paper border-clay shadow-md"
              : "bg-paper/90 text-ink border-line hover:bg-paper"
          )}
        >
          <Tag className="w-3.5 h-3.5" />
          <span>{allOpen ? "Hide price tags" : "Show price tags"}</span>
        </button>
      </div>
    </div>
  );
}
